import React, { useState } from 'react';
import { Button } from 'react-bootstrap';
import { MARKET, TEXT, marketEvidenceText, marketRangeText, marketUnitsText, marketPriceHint, marketAverageText, guestsText } from '../../config/listingPerformanceLabels';
import { Figure, Hint, formatFigure } from './parts';

/**
 * «مقابل السوق» — our guest price against similar units on Airbnb (tasks/market-price-comparison-build.md step 6).
 * Staff view only; the server never sends it to the owner view.
 *
 * Read in one glance: a word and one figure — «أعلى 18.1%» · «في السوق» · «أقل 12.0%» — or, when there is no
 * comparison, the plain reason word («غير مؤكد», «غير كافٍ»…). The hover gives the evidence (our price, the similar
 * units' price and range, how many nights and units) or the reason in a sentence. Neutral ink: it is a pointer for
 * a price review, never a verdict or a target (founder, 22 Oct). «أدلة محدودة» under the word when the verdict rests
 * on few similar units or few nights (D12).
 *
 * Every figure and every status is the server's — nothing is worked out here.
 */

const fmt = (v) => formatFigure(v, Number.isInteger(v) ? 'count' : 'money');
const hasVerdict = (cell) => cell && typeof cell.gapPct === 'number';

/** «—» with the reason as its hint. */
const Dash = ({ hint }) => <Hint text={hint}><span className="lp-missing">—</span></Hint>;

const pct = (v) => formatFigure(v, 'pct1');

/** The word, with the gap when above or below. */
const MarketWord = ({ cell }) => {
  if (cell.status === 'above' || cell.status === 'below') {
    return (
      <span>
        {cell.status === 'above' ? MARKET.above : MARKET.below}{' '}
        <bdi className="lp-num">{formatFigure(Math.abs(cell.gapPct), 'pct1')}</bdi>
      </span>
    );
  }
  return <span>{MARKET.status[cell.status] || cell.status}</span>;
};

/** The board cell and the unit detail's «مقابل السوق» row. null = no comparison that week. */
export const MarketCell = ({ cell }) => {
  if (!cell) return <Dash hint={MARKET.noRun} />;
  const verdict = hasVerdict(cell);
  const weak = verdict && (cell.indicative || cell.limitedEvidence);
  const hint = verdict ? marketEvidenceText(cell, fmt) : MARKET.hint[cell.reasonCode || cell.status];
  return (
    <Hint text={hint}>
      <span className={`lp-market${verdict ? '' : ' lp-market--none'}`}>
        <MarketWord cell={cell} />
        {weak && <span className="lp-market__weak">{MARKET.limited}</span>}
      </span>
    </Hint>
  );
};

/** The similar units' price (median), with how many units and their middle range under it, in words. */
const MarketPriceFigure = ({ cell }) => {
  if (!hasVerdict(cell)) return <Dash hint={cell ? MARKET.hint[cell.reasonCode || cell.status] || MARKET.noRun : MARKET.noRun} />;
  return (
    <Hint text={marketPriceHint(cell.competitorsMin, fmt)}>
      <span className="lp-market">
        <Figure value={cell.marketMedian} kind="money" />
        <span className="lp-market__range">{marketUnitsText(cell.competitorsMin, fmt, cell.competitorsListed)}، {marketRangeText(cell, fmt)}</span>
      </span>
    </Hint>
  );
};

/** The 4-week mean of the gap, in the same words as the other columns; the figure and weeks on hover. */
const AverageGap = ({ average }) => {
  if (!average || typeof average.gapPct !== 'number') return <Dash hint={MARKET.noRun} />;
  return (
    <Hint text={marketAverageText(average.weeks, average.gapPct, pct)}>
      <span className="lp-market"><MarketWord cell={average} /></span>
    </Hint>
  );
};

/**
 * The group's approved competitors, folded: one link per listing, labelled by its guest capacity — ops open the
 * cheaper ones on Airbnb in a price review. The approved list as it stands today; no price (prices stay off the page).
 */
const CompetitorList = ({ group }) => {
  const [open, setOpen] = useState(false);
  if (!group?.competitors?.length) return null;
  return (
    <tr className="lp-market-comps">
      <td colSpan={4}>
        <Button variant="link" size="sm" className="lp-link" aria-expanded={open} onClick={() => setOpen(o => !o)}>
          <i className={`bi ${open ? 'bi-chevron-up' : 'bi-chevron-down'}`} aria-hidden="true" />{' '}
          {open ? MARKET.hideCompetitors : MARKET.showCompetitors(group.competitors.length)}
        </Button>
        {open && (
          <>
            <p className="lp-muted lp-market-comps__hint">{MARKET.competitorsHint}</p>
            <ul className="lp-market-comps__list">
              {group.competitors.map(c => (
                <li key={c.airbnbId}>
                  <a
                    href={`https://www.airbnb.com/rooms/${c.airbnbId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="lp-market-comps__link"
                    title={MARKET.openOnAirbnb}
                  >
                    <i className="bi bi-house-door" aria-hidden="true" /> {guestsText(c.guests)}
                  </a>
                </li>
              ))}
            </ul>
          </>
        )}
      </td>
    </tr>
  );
};

/** The unit detail's two rows, in the «التقييم والسعر» group: this week · prior week · 4-week mean. */
export const MarketRows = ({ market }) => (
  <>
    <tr>
      <th scope="row">{MARKET.priceRow}</th>
      <td data-label={TEXT.thisWeek} className="lp-detail__now"><MarketPriceFigure cell={market.current} /></td>
      <td data-label={TEXT.priorWeek}><MarketPriceFigure cell={market.prior} /></td>
      <td data-label={TEXT.fourWeekAvg}><Figure value={market.average?.marketMedian ?? null} kind="money" /></td>
    </tr>
    <tr>
      <th scope="row">{MARKET.gapRow}</th>
      <td data-label={TEXT.thisWeek} className="lp-detail__now"><MarketCell cell={market.current} /></td>
      <td data-label={TEXT.priorWeek}><MarketCell cell={market.prior} /></td>
      <td data-label={TEXT.fourWeekAvg}><AverageGap average={market.average} /></td>
    </tr>
    <CompetitorList group={market.group} />
  </>
);
